import { zodResolver } from "@hookform/resolvers/zod";
import { Button, IconButton } from "@hortis/ui/button";
import { DialogTrigger } from "@hortis/ui/dialog";
import { ImagePlus } from "@hortis/ui/icons";
import { Tooltip, TooltipContent, TooltipTrigger } from "@hortis/ui/tooltip";
import { useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { stopEventPropagation } from "src/features/collection/components/stop-event-propagation";
import { MaterialSchema } from "../add-plant-material-form/schema";
import { AddMaterialPhotosButton } from "./add-material-photos-button";
import { DiscardDialog } from "./discard-dialog";
import {
  MaterialNestedFields,
  materialDefaultValues,
} from "./material-nested-fields";
import { UploadsInProgressDialog } from "./uploads-in-progress-dialog";
import { useStagedMaterialPhotos } from "./use-staged-material-photos";

export const MaterialFields = ({
  initialValue,
  defaultValues: _defaultValues = {},
  onSubmit,
  onCancel,
  siteSlug,
}: {
  initialValue?: MaterialSchema;
  defaultValues?: Partial<MaterialSchema>;
  onSubmit: (data: MaterialSchema) => void;
  onCancel: () => void;
  siteSlug: string;
}) => {
  const {
    stagedPhotos,
    setStagedPhotos,
    uploadsInProgressDialogOpen,
    setUploadsInProgressDialogOpen,
    processStagedPhotos,
  } = useStagedMaterialPhotos(initialValue?.images);

  const defaultValues = useMemo(() => {
    if (initialValue != null) {
      const { images: _, ...rest } = initialValue;
      return rest;
    }
    return { ...materialDefaultValues, ..._defaultValues };
  }, [initialValue, _defaultValues]);

  const form = useForm<MaterialSchema>({
    defaultValues,
    resolver: zodResolver(MaterialSchema),
  });

  const handleSubmit = () => {
    const processedPhotos = processStagedPhotos();
    if (processedPhotos == null) {
      return;
    }
    form.setValue("images", processedPhotos);
    void form.handleSubmit((data: Partial<MaterialSchema>) => {
      onSubmit(data as MaterialSchema);
    })();
  };

  return (
    <FormProvider {...form}>
      <UploadsInProgressDialog
        open={uploadsInProgressDialogOpen}
        onOpenChange={setUploadsInProgressDialogOpen}
        onDiscard={() => {
          handleSubmit();
        }}
      />
      <MaterialNestedFields
        siteSlug={siteSlug}
        form={form}
        stagedPhotos={stagedPhotos}
        setStagedPhotos={setStagedPhotos}
      />
      <div className="flex items-center justify-between gap-2 border-t border-grey-200 py-2 pl-3 pr-4">
        <AddMaterialPhotosButton
          images={stagedPhotos}
          setImages={setStagedPhotos}
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <IconButton
                icon={ImagePlus}
                size="sm"
                variant="tertiaryGray"
                ariaLabel="Add photos"
                testId="add-material-photo-button"
                onFocusCapture={stopEventPropagation}
                tabIndex={-1}
              />
            </TooltipTrigger>
            <TooltipContent side="bottom" sideOffset={0}>
              Add photos
            </TooltipContent>
          </Tooltip>
        </AddMaterialPhotosButton>
        <div className="flex items-center gap-2">
          <Tooltip>
            <DiscardDialog onDiscard={onCancel}>
              <DialogTrigger
                asChild
                onClick={
                  Object.keys(form.formState.dirtyFields).length > 0 ||
                  stagedPhotos.length > 0
                    ? undefined
                    : onCancel
                }
              >
                <TooltipTrigger asChild>
                  <Button
                    variant="tertiaryGray"
                    testId="cancel-material-button"
                    onFocusCapture={stopEventPropagation}
                  >
                    Cancel
                  </Button>
                </TooltipTrigger>
              </DialogTrigger>
              <TooltipContent side="bottom" sideOffset={0}>
                Discard changes
              </TooltipContent>
            </DiscardDialog>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="primary"
                onClick={handleSubmit}
                testId="save-material-button"
                onFocusCapture={stopEventPropagation}
              >
                {initialValue == null ? "Add material" : "Save material"}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" sideOffset={0}>
              Add material to accession
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    </FormProvider>
  );
};
